import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';

import PopupWarning from '../../../components/Popup/PopupWarning';
import SkipRegistrationButton from '../../../components/SkipRegistrationButton/SkipRegistrationButton';
import appConfig from '../../../config/appConfig';
import * as loaderNames from '../../../consts/loader/loaderNames';
import * as localizationKeys from '../../../consts/localization/localizationKeys';
import ModalTypes from '../../../enums/app/modalTypes';
import {amplitudeRegistrationTracking} from '../../../services/amplitude';
import analyticsService from '../../../services/analyticsService';
import appErrorService from '../../../services/app/appErrorService';
import connectivityService from '../../../services/device/connectivityService';
import {getLocalizedStrings} from '../../../services/localization/localizationService';
import marketConfigService from '../../../services/marketConfigService';
import modalService from '../../../services/modalService';
import pairingService from '../../../services/pairingService';
import productService from '../../../services/product/productService';
import appRouterService from '../../../services/route/appRouterService';
import routerService from '../../../services/routerService';
import yapConnectionService from '../../../services/yap/yapConnectionService';
import {hideLoader, setIsSkipRegistration, showLoader} from '../../../state/ducks/global';
import {showHidReconnectModalAction} from '../../../state/ducks/modal';
import {removeProductToChangeRegistrationStatus} from '../../../state/ducks/productSettings';
import {makeSelectIotDeviceMergedWithConsumerProduct} from '../../../state/selectors/consumer';
import {selectIsStartAutoSync, selectLoaders} from '../../../state/selectors/global';
import {selectIsDeviceReady} from '../../../state/selectors/iotDevice';
import {dispatch} from '../../../state/store';

const RegistrationHolder = (props) => {
    const {isPopupModalMode = true, regAction} = props;
    const iotDevice = useSelector(makeSelectIotDeviceMergedWithConsumerProduct());
    const isDeviceReady = useSelector(selectIsDeviceReady);
    const isStartAutoSync = useSelector(selectIsStartAutoSync);
    const {device, holder} = iotDevice || {};
    const {isHolderConnected, type, isP4} = device || {};
    const {name: holderName, codentify, deviceSerialNumber} = holder || {};
    const isUsb = connectivityService.isUsb();
    const isDeviceRegistrationEnabled = marketConfigService.isDeviceRegistrationEnabled();
    const isSkipRegistrationEnabled = productService.isSkipRegistrationEnabled(isP4);
    const isShowSkipBtn = isSkipRegistrationEnabled && !isDeviceRegistrationEnabled;
    const loaders = useSelector(selectLoaders);
    const isAnyLoaders = loaders?.length > 0;
    const localizedStrings = getLocalizedStrings();

    const checkHolderConnection = async () => {
        const {holderSerial, holderCodentify} = await yapConnectionService.initScpData({
            isHolderOnly: true,
            initialDeviceType: type,
        });

        const isHolderExists = holderCodentify || holderSerial;

        return {holderSerial, holderCodentify, isHolderExists};
    };

    const registrationAction = (data) => {
        if (regAction) {
            regAction(data);
            return;
        }

        productService.purchaseRegisterDeviceCheck({
            action: (purchasedOn) => {
                dispatch(showLoader(loaderNames.REGISTERING_LOADER));
                const registerData = {name: holderName, deviceSerialNumber, codentify, isHolder: true, purchasedOn};

                productService.registerDevice(registerData).then((res) => {
                    if (!res || !holder) {
                        dispatch(hideLoader(loaderNames.REGISTERING_LOADER));
                    }
                });
            },
        });
    };

    const checkHolder = async () => {
        const {holderSerial, holderCodentify, isHolderExists} = await checkHolderConnection();

        if (isHolderExists) {
            dispatch(
                removeProductToChangeRegistrationStatus({
                    deviceSerialNumber: holderSerial,
                    codentify: holderCodentify,
                })
            );
            registrationAction({isHolder: true, serialNumber: holderSerial, codentifyId: holderCodentify});
        } else {
            if (!isStartAutoSync) {
                appRouterService.forwardToDeviceSyncPage();
            }
        }
    };

    const startHolderRegistration = async () => {
        if (!isDeviceRegistrationEnabled) {
            const {isHolderExists} = await checkHolderConnection();

            if (isHolderExists) {
                const redirectUrl = appConfig.getProductRegistrationUrlFormatted();

                amplitudeRegistrationTracking.trackRegistrationStarted(true);
                dispatch(showLoader(loaderNames.REGISTERING_LOADER));
                routerService.redirect(redirectUrl);
            } else {
                appRouterService.forwardToDeviceSyncPage();
            }
            return;
        }

        if (!isDeviceReady && isStartAutoSync) {
            if (isUsb) {
                dispatch(showHidReconnectModalAction());
            } else {
                appErrorService.showDeviceDisconnectNotification();
            }
            return;
        }

        await checkHolder();
        if (isPopupModalMode) {
            modalService.hideModal(ModalTypes.REGISTRATION_HOLDER);
        }
    };

    useEffect(() => {
        if (!isHolderConnected && isPopupModalMode) {
            modalService.hideModal(ModalTypes.REGISTRATION_HOLDER);
        }
    }, [isHolderConnected]);

    useEffect(() => {
        if (!isAnyLoaders) {
            analyticsService.pushRegistrationUnregisteredHolderEvent(type);
        }
    }, []);

    const descriptionKey = isDeviceRegistrationEnabled
        ? localizationKeys.UNREGISTERED_HOLDER_INSERTED_POPUP_DESCRIPTION
        : localizationKeys.UNREGISTERED_HOLDER_INSERTED_POPUP_NON_NATIVE_REGISTER_DESCRIPTION;

    const startRegKey = isPopupModalMode
        ? localizationKeys.UNREGISTERED_HOLDER_INSERTED_POPUP_REGISTER_BUTTON
        : localizationKeys.CONNECTION_UNREGISTERED_HOLDER_INSERTED_POPUP_REGISTER_BUTTON;

    const onClose = () => {
        pairingService.disconnectAndForwardToMyDevicesPage();
    };
    const cancelBtn = isShowSkipBtn
        ? undefined
        : {
              onClick: onClose,
              textKey: localizationKeys.UNREGISTERED_HOLDER_INSERTED_POPUP_CANCEL_BUTTON,
          };

    const onSkipRegClick = async () => {
        const {isHolderExists} = await checkHolderConnection();

        if (isPopupModalMode) {
            modalService.hideModal(ModalTypes.REGISTRATION_HOLDER);
        } else if (!isHolderExists) {
            //if no holder then skip reg should be worked as continue btn and just redirect to sync page
            dispatch(setIsSkipRegistration(false));
        }
    };

    return (
        <PopupWarning
            contentLabel={localizedStrings[localizationKeys.UNREGISTERED_HOLDER_INSERTED_POPUP_TITLE]}
            titleKey={localizationKeys.UNREGISTERED_HOLDER_INSERTED_POPUP_TITLE}
            descriptionKey={descriptionKey}
            buttonsDirection='column'
            buttonPrimary={{
                isConnectionTrack: true,
                onClick: startHolderRegistration,
                textKey: startRegKey,
            }}
            buttonSecondary={cancelBtn}
        >
            {isShowSkipBtn && <SkipRegistrationButton onSkipClick={onSkipRegClick} />}
        </PopupWarning>
    );
};

export default RegistrationHolder;
